import React from "react";
import { createPortal } from "react-dom";
import {
  X, Compass, Users, FileSpreadsheet, Table, Scale, ClipboardCheck,
  CalendarClock, Activity, GitCompareArrows, Upload, Layers, Ship,
  School, ShieldCheck, ClipboardList, ScanLine, TrendingUp, AlertTriangle,
  FileText, Share2, BookOpen, Sparkles,
} from "lucide-react";
import useEscClose from "../hooks/useEscClose";

/**
 * FeatureGuideModal — new-user orientation. Opens automatically on the
 * first login of each browser (localStorage flag) and can be re-opened
 * any time from the user menu.
 *
 * Design choices:
 * - Grouped by workflow, not by menu location — a coach thinks in tasks
 *   ("I want to import a lab report"), not tabs.
 * - Every card mentions the primary UI location so users can jump right
 *   after reading. Names match the actual tab / menu labels exactly.
 * - No product marketing. Direct verbs, one sentence per feature.
 */

const SECTIONS = [
  {
    title: "Start here — the main tabs",
    intro:
      "The tab strip along the top drives the whole app. Each tab is a different lens on the same underlying data.",
    items: [
      {
        icon: Compass, colour: "text-amber-300",
        title: "Squad Overview",
        body: "Aggregate dashboard — deficiency heatmaps, injury snapshot, key metrics across the whole squad.",
      },
      {
        icon: Activity, colour: "text-cyan-300",
        title: "Athlete Trend",
        body: "Longitudinal timeline for one athlete — every lab marker, biometric, injury and consultation on a single time axis.",
      },
      {
        icon: GitCompareArrows, colour: "text-emerald-300",
        title: "Compare Athletes",
        body: "Side-by-side comparison of markers across multiple sailors. Useful for benchmarking within a class or squad.",
      },
      {
        icon: FileSpreadsheet, colour: "text-fuchsia-300",
        title: "Data Editor",
        body: "Per-athlete CRUD. Add lab records, consultations, supplements, deworming and injuries. Click Scan on any lab row to open the original PDF.",
      },
      {
        icon: Table, colour: "text-cyan-300",
        title: "Data Entry Log",
        body: "Every entry across every athlete in one wide grid. Excel-style inline edit with autosave.",
      },
      {
        icon: ClipboardCheck, colour: "text-lime-300",
        title: "Baselines",
        body: "One row per athlete showing their baseline profile (DOB, gender, class, parents' heights, medical history). Double-click to edit the full form.",
      },
      {
        icon: Scale, colour: "text-pink-300",
        title: "Biometrics",
        body: "Height, weight, BMI matrix across dates. Sticky Father / Mother / Predicted columns on the left — edit parents' heights inline and the predicted adult height (Bayer-Bayley + Tanner blend) updates instantly. A Rate chip next to each name shows cm/mo and kg/mo growth or loss.",
      },
      {
        icon: CalendarClock, colour: "text-amber-300",
        title: "Schedule",
        body: "Auto-generated 3-month diagnostic reminders and 6-month deworming schedule. Snooze or dismiss per athlete.",
      },
    ],
  },
  {
    title: "Get data into the app",
    intro:
      "Three ways to load athletes and lab data. Pick what fits the situation.",
    items: [
      {
        icon: Users, colour: "text-cyan-300",
        title: "Add an athlete",
        body: "Data Editor → New athlete. Fill baseline immediately or later — most fields are optional at create time.",
      },
      {
        icon: ScanLine, colour: "text-fuchsia-300",
        title: "Lab-scan import (single PDF)",
        body: "User menu → Lab-scan import. Drop a Tata 1mg PDF — the deterministic parser extracts every marker in ~500 ms, falls back to Claude if the layout is unknown. The original PDF is archived and viewable later from the Data Editor.",
      },
      {
        icon: Upload, colour: "text-pink-300",
        title: "Bulk lab import",
        body: "User menu → Bulk lab import. Drop up to 50 PDFs at once, fuzzy-matches each to an existing athlete, review the grid, commit selected rows.",
      },
    ],
  },
  {
    title: "Masters — controlled vocabularies",
    intro:
      "Rename any master here and it cascades onto every athlete carrying that value.",
    items: [
      {
        icon: Users, colour: "text-cyan-300",
        title: "Squads master",
        body: "The squad each athlete belongs to. Single-value per athlete.",
      },
      {
        icon: Ship, colour: "text-amber-300",
        title: "Classes master",
        body: "Boat classes (Optimist, ILCA 6, Waszp, 29er…). Multi-value — a sailor can hold two classes as chips.",
      },
      {
        icon: Layers, colour: "text-emerald-300",
        title: "Levels master",
        body: "Competitive tier: Elite / National Squad / Beginner. Single-value.",
      },
      {
        icon: School, colour: "text-cyan-300",
        title: "Schools master",
        body: "Feeder schools for demographic reporting.",
      },
      {
        icon: ClipboardList, colour: "text-fuchsia-300",
        title: "Bulk assign",
        body: "Data Editor → Bulk assign. Excel-style grid with 500 ms autosave — set squad, level, class chips across dozens of athletes at once.",
      },
    ],
  },
  {
    title: "Health insights",
    intro:
      "Everything the app tries to do for you once your data is in.",
    items: [
      {
        icon: TrendingUp, colour: "text-cyan-300",
        title: "Predicted adult height",
        body: "Blends Bayer-Bayley chronological-age curve with Tanner mid-parental target. Colour-coded chip in the Biometrics table shows the source and confidence.",
      },
      {
        icon: AlertTriangle, colour: "text-rose-300",
        title: "Out-of-range markers",
        body: "Every lab value is stamped against age/gender-aware reference ranges. OOR count and weighted severity index appear on trend charts and drill-down modals.",
      },
      {
        icon: Sparkles, colour: "text-fuchsia-300",
        title: "AI nutrition suggestions",
        body: "Claude-generated food swaps tiered Red / Amber / Green by clinical severity. Cached per athlete so you're not billed for repeats.",
      },
      {
        icon: BookOpen, colour: "text-emerald-300",
        title: "Daily AI Coach briefing",
        body: "One-line status on every athlete flagged for the day. Opens automatically at login (dismiss to keep it out of your face).",
      },
    ],
  },
  {
    title: "Data quality & admin",
    intro:
      "Housekeeping features — mostly super-admin territory.",
    items: [
      {
        icon: ShieldCheck, colour: "text-amber-300",
        title: "Data Quality Check",
        body: "User menu → Data Quality. Flags impossible values, big weight swings, height reductions, duplicate athletes (same name / same DOB), duplicate lab uploads. Click Fix on any row for a pre-filled correction.",
      },
      {
        icon: FileText, colour: "text-cyan-300",
        title: "Reference ranges",
        body: "User menu → Reference Ranges. Coloured panels grouped by system (CBC / Iron / Vitamins / Urine). Override any range without a code deploy.",
      },
      {
        icon: FileText, colour: "text-fuchsia-300",
        title: "Audit log",
        body: "User menu → Audit log. Every write is stamped with the operator and preserves before/after values.",
      },
      {
        icon: FileText, colour: "text-pink-300",
        title: "Backup & restore",
        body: "User menu → Backup. Full-database JSON export and restore for super-admins.",
      },
    ],
  },
  {
    title: "Reports & sharing",
    intro:
      "For parent meetings, medical exports and print-ready summaries.",
    items: [
      {
        icon: FileText, colour: "text-amber-300",
        title: "Medical history PDF",
        body: "Athlete Trend → Print icon. One-click, universally printable medical history including baseline, labs, injuries, consultations.",
      },
      {
        icon: Share2, colour: "text-emerald-300",
        title: "Share link",
        body: "Any athlete report → Share. Generates a time-boxed WhatsApp/email link that opens the report without requiring the recipient to log in.",
      },
    ],
  },
];

const STORAGE_KEY = "ych:feature-guide-seen";

export function shouldAutoShowFeatureGuide() {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — never auto-show to avoid a
    // popup on every login, but the user menu button still works.
    return false;
  }
}

export function markFeatureGuideSeen() {
  try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
}

export default function FeatureGuideModal({ open, onClose }) {
  useEscClose(open, onClose);
  if (!open) return null;

  const close = () => {
    markFeatureGuideSeen();
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-8"
      style={{ backdropFilter: "blur(8px)", background: "rgba(11,22,39,0.78)" }}
      onClick={close}
      data-testid="feature-guide-modal"
    >
      <div
        className="card w-full max-w-5xl mx-4 p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-100"
          data-testid="feature-guide-close"
          aria-label="Close feature guide"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2.5 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300">
            <Compass size={22} />
          </div>
          <div>
            <div className="font-display text-2xl text-slate-100 tracking-tight">
              What&apos;s in this app
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              A quick tour for new users — every feature grouped by workflow.
              You can reopen this from the user menu any time.
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-8">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <div className="text-[11px] uppercase tracking-widest text-cyan-300/70 font-semibold">
                {section.title}
              </div>
              <p className="text-[12px] text-slate-400 mt-1 mb-3 leading-relaxed">
                {section.intro}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {section.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <div
                      key={it.title}
                      className="flex items-start gap-3 p-3 rounded-lg border border-[#1E304B] bg-[#0E1A2E]/40 hover:bg-[#0E1A2E]/70 transition-colors"
                    >
                      <div className={`shrink-0 rounded-md p-1.5 bg-[#0B1627] border border-[#1E304B] ${it.colour}`}>
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] text-slate-100 font-semibold">
                          {it.title}
                        </div>
                        <div className="text-[11.5px] text-slate-400 leading-relaxed mt-0.5">
                          {it.body}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-8 pt-5 border-t border-[#1E304B] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500 leading-relaxed max-w-xl">
            Missing something you expected? Data lives in Mongo + GridFS
            behind a mobile-PIN auth wall. Every edit is stamped with the
            operator and preserved in the audit log.
          </div>
          <button
            onClick={close}
            className="btn-primary text-[12px] whitespace-nowrap"
            data-testid="feature-guide-got-it"
          >
            Got it — take me in
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
